import * as PropTypes from "prop-types";
import { connect } from 'react-redux';
import { ActionsRow, FixedDropDownButton, Icon, Loader, utils, authActions } from "@mc/mc-legacy";
import { Alert, Button, ButtonToolbar, Card, CardBody, CardHeader, DropdownItem, Table } from "reactstrap";
import { toastr } from "react-redux-toastr";
import Dropzone from "react-dropzone";
import { getDroppedOrSelectedFiles } from 'html5-file-selector';
import * as ciselnikActions from "../actions/ciselnikActions";
import CustomCheckbox from "./CustomCheckbox";
import ObjectSelect from "./ObjectSelect";
import {trimStart} from "lodash-es";
import {Component} from "react";
import * as React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import './FileUploadPanel.scss';

/**
 * Redux-form kompatibilní panel pro nahrávání souborů, umožňující zadávat k jednotlivým souborům hodnoty nadefinovaných atributů.
 *
 * Jako hodnotu očekává a vrací pole objektů, obsahující vždy soubor (pokud ještě nebyl odeslán na server)
 * NEBO název a dočasný kód souboru (pokud již byl odeslán na server) a příslušná metadata (zatím jen souborové atributy).
 * Příklady:
 *
 * [
 *  {
 *      file: [File],
 *      attributes: {
 *          atributId_1: hodnotaAtributId_1,
 *          atributId_2: hodnotaAtributId_2,
 *          ...
 *      }
 *  },
 *  {
 *      name: "dokument1.pdf",
 *      code: "5aa947a6-a678-4a62-b28a-44b27bb1cc93",
 *      attributes: {
 *          atributId_1: hodnotaAtributId_1,
 *          atributId_2: hodnotaAtributId_2,
 *          ...
 *      }
 *  },
 *  ...
 * ]
 */
class FileUploadPanel extends Component<WithTranslation> {

    // TODO použití se změnilo tak že soubory se teď uploadují ihned - komponenta by díky tomu šla zjednodušit jelikož už nepotřebujeme logiku pro lokálně uložené soubory

    static propTypes = {
        input: PropTypes.object.isRequired, // redux-form input objekt
        uploading: PropTypes.bool, // nepovinný příznak že probíhá nahrávání souborů, pokud je true, zobrazí se upozornění o probíhajícím nahrávání
    };

    static defaultProps = {
        uploading: false,
    };

    constructor() {
        super();
        this.state = {
            checkboxes: {},
            allChecked: false,
            dragActive: false,
            droppedFolder: null
        };
    }

    componentDidMount() {
        this.fetchData();
        this.initDirectoryInput();
    }

    componentDidUpdate() {
        this.fetchData();
        this.initDirectoryInput();
    }

    initDirectoryInput = () => {
        if (this.fileInput) {
            this.fileInput.directory = true;
            this.fileInput.webkitdirectory = true;
            this.fileInput.multiple = true;

            this.fileInput.addEventListener("change", async (event) => {
                let { files } = event.target;
                files = [...files];

                // ignore .DS_store file
                files = files.filter((f) => !f.name.startsWith('.'));

                this.handleFilesDropped(files);
            }, false);

            this.fileInput.value = '';
        }
    };

    fetchData = () => {
        this.props.dispatch(ciselnikActions.fetchSouborAtributyIfNeeded());
    };

    handleFilesDropped = (files) => {
        if (this.state.droppedFolder || (files && files.length > 0)) {
            this.addFiles(this.state.droppedFolder || files);
        }
    };

    addFiles = (addedFiles) => {
        const { input: { value, onChange }, authInfo, t } = this.props;

        // soubory z drag'n'drop mají na začátku lomítko navíc, což způsobuje problémy při kontrole duplicit
        const files = [...addedFiles];
        files.forEach(f => {
            if (f.fullPath) f.fullPath = trimStart(f.fullPath, '/');
        });

        // Kontrola velikosti a duplicit
        if (files) {
            const maxFileSizeMB = authInfo && authInfo.data && authInfo.data.moduleInfos && authInfo.data.moduleInfos.CGO
                && authInfo.data.moduleInfos.CGO.configuration && authInfo.data.moduleInfos.CGO.configuration.maxFileSize;
            const maxFileSize = maxFileSizeMB && (maxFileSizeMB * 1000000);
            const tooLargeNames = [];
            let badIndexes = [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                if (f.size > maxFileSize) {
                    tooLargeNames.push(f.fullPath || f.webkitRelativePath);
                    badIndexes.push(i);
                }
            }
            if (tooLargeNames.length !== 0) {
                for (let i = badIndexes.length - 1; i >= 0; i--) {
                    files.splice(badIndexes[i], 1);
                }
                toastr.error(t('validation.filesize_limit_reached', {maxFileSizeMB}), tooLargeNames.join(", "), {timeOut: 0});
                this.setState({ dragActive: false, droppedFolder: null }); // z nějakého důvodu zůstává po dropu souborů true
                return;
            }
            const duplicatesNames = [];
            badIndexes = [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                // soubory z drag'n'drop mají fullPath, "naklikané" mají webkitRelativePath
                const fPath = f.fullPath || f.webkitRelativePath;
                const duplicate = value && value.find(v => {
                    const vPath = v.podslozky ? `${v.podslozky}${v.name}` : v.name;
                    return vPath === fPath;
                });
                if (duplicate) {
                    duplicatesNames.push(fPath);
                    badIndexes.push(i);
                }
            }
            // vyházení duplicit
            if (duplicatesNames.length !== 0) {
                for (let i = badIndexes.length - 1; i >= 0; i--) {
                    files.splice(badIndexes[i], 1);
                }
                toastr.error(t('validation.files_already_uploaded'), duplicatesNames.join(", "));
            }
        }
        onChange(value ? [...value, ...files] : files);
        this.setState({ dragActive: false, droppedFolder: null }); // z nějakého důvodu zůstává po dropu souborů true
    };

    removeFile = (fileMeta) => {
        const { input: { value, onChange } } = this.props;
        const fileName = fileMeta.file
            ? fileMeta.file.name
            : fileMeta.podslozky + fileMeta.name;
        const fileMetas = [...value];
        const index = fileMetas.indexOf(fileMeta);
        const checkboxes = { ...this.state.checkboxes };
        delete checkboxes[fileName];

        fileMetas.splice(index, 1);
        this.setState({ checkboxes });
        onChange(fileMetas);
    };

    changeCheckbox = (fileName) => {
        const { input: { value } } = this.props;
        const checkboxes = { ...this.state.checkboxes };
        checkboxes[fileName] = !checkboxes[fileName];

        let allChecked = Object.keys(checkboxes).length === value.length;
        for (let key of Object.keys(checkboxes)) {
            if (!checkboxes[key]) {
                allChecked = false;
                break;
            }
        }
        this.setState({ checkboxes, allChecked });
    };

    changeAllCheckbox = (e) => {
        const { input: { value } } = this.props;
        const checked = e.target.checked;
        const checkboxes = { ...this.state.checkboxes };
        value.forEach(fileMeta => {
            const fileName = fileMeta.file
                ? fileMeta.file.name
                : fileMeta.podslozky + fileMeta.name;

            checkboxes[fileName] = checked;
        });
        this.setState({ checkboxes, allChecked: checked });
    };

    handleOpenSoubor = (fileMeta) => {
        if (fileMeta.file) { // lokální, neuploadovaný soubor
            const url = URL.createObjectURL(fileMeta.file);
            const fileLink = document.createElement('a');
            fileLink.href = url;
            fileLink.setAttribute('download', fileMeta.file.name);
            fileLink.click();
            URL.revokeObjectURL(url);
        } else { // již uploadovaný soubor
            window.open(serverContextPath + "/files/temp/" + fileMeta.code);
        }
    };

    handleAttributeChanged = (name, attribute, e) => {
        const { input: { value } } = this.props;
        const { checkboxes } = this.state;
        const attributeValue = e.id ? e : null;
        // Aktualizujeme atributy souborů ve state - sobě + všem které mají zaškrtnutý checkbox
        const newFileMetas = [...value];
        for (let fileMeta of newFileMetas) {
            const fileName = fileMeta.file
                ? fileMeta.file.name
                : fileMeta.podslozky + fileMeta.name;
            if (fileName === name || checkboxes[fileName]) {
                fileMeta.attributes[attribute.id] = attributeValue;
            }
        }
    };

    handleDragEnter = () => {
        this.setState({ dragActive: true });
    };

    handleDragLeave = () => {
        this.setState({ dragActive: false });
    };

    renderSelectMenu = () => {
        const {t} = this.props;
        return (
            <div className="soubory-dropzone-actions">
                <ActionsRow actions={
                    <FixedDropDownButton color="primary" noCaret title={<span>{t('choose')} <Icon icon="caret-down" /></span>}>
                        <DropdownItem key="0" onClick={() => this.dropzoneRef.open()}>{t('choose_file')}</DropdownItem>
                        <DropdownItem key="1" onClick={() => this.fileInput.click()}>
                            <input
                                ref={(node) => this.fileInput = node}
                                style={{ display: "none" }}
                                type='file'
                            />
                            {t('choose_folder')}
                        </DropdownItem>
                    </FixedDropDownButton>}>
                </ActionsRow>
            </div>
        );
    };

    render() {
        const { input: { value, name }, souborAtributyList, uploading, t } = this.props;
        const { checkboxes, allChecked, dragActive } = this.state;

        if (!souborAtributyList.fetched) {
            return <Loader />;
        }

        const hasSoubory = Array.isArray(value) && value.length > 0;

        let dropzoneContent;
        if (dragActive) {
            dropzoneContent = <div className={"soubory-dropzone-dragging"}>{t('drag_files_folder')}</div>
        } else if (!hasSoubory) {
            dropzoneContent = <div className={"soubory-dropzone-selection"}>
                <div>{t('drag_files_folder')}</div>
                <div>{t('or').toLowerCase()}</div>
                {this.renderSelectMenu()}
            </div>
        } else if (uploading) {
            dropzoneContent = <Alert color="info" className={"w-100"}><Loader>{t('files_uploading')}</Loader></Alert>
        } else {
            dropzoneContent = <div>
                <div className="soubory-dropzone-hint">
                    {t('drag_files_folder')}
                </div>
                <div className="soubory-dropzone-table-wrapper">
                    <Table>
                        <thead>
                        <tr>
                            <th>
                                <CustomCheckbox onChange={this.changeAllCheckbox} value={allChecked} />
                            </th>
                            <th />
                            {souborAtributyList.rows.map(atribut => <th
                                key={atribut.id}>{atribut.nazevAtribut}</th>)}
                            <th />
                        </tr>
                        </thead>
                        <tbody className="items-list">
                            {value.length > 0 && value.map((fileMeta, index) => {
                                const fileName = fileMeta.file
                                    ? fileMeta.file.name
                                    : fileMeta.name;
                                const fileFullName = fileMeta.podslozky + fileName;

                                return <tr className="card-block actions-hover" key={index}>
                                    <td>
                                        <CustomCheckbox
                                            onChange={this.changeCheckbox.bind(this, fileFullName)}
                                            value={checkboxes[fileFullName] ? checkboxes[fileFullName] : false} />
                                    </td>
                                    <td>{fileFullName}</td>
                                    {souborAtributyList.rows.map(atribut => {
                                        return <th key={atribut.id}>
                                            <ObjectSelect
                                                label={false}
                                                name={`${name}[${index}].attributes.${atribut.id}`}
                                                items={atribut.hodnoty}
                                                itemValueAttrName="hodnotaAtribut"
                                                onChange={this.handleAttributeChanged.bind(this, fileFullName, atribut)}
                                            />
                                        </th>
                                    })}
                                    <td className="actions">
                                        <Button onClick={this.handleOpenSoubor.bind(this, fileMeta)}
                                                color="action action-hover" title={t('download_this_file')}>
                                            <Icon icon="download" />
                                        </Button>
                                        <Button onClick={this.removeFile.bind(this, fileMeta)}
                                                color="action action-hover" title={t('delete_this_file')}>
                                            <Icon icon="times" />
                                        </Button>
                                    </td>
                                </tr>
                            })}
                        </tbody>
                    </Table>
                </div>
            </div>
        }

        return <Card>
            <CardHeader>
                <ActionsRow actions={
                    hasSoubory && <ButtonToolbar>
                        {this.renderSelectMenu()}
                    </ButtonToolbar>}
                >
                    <h2>{t('uploaded_files')}</h2>
                </ActionsRow>
            </CardHeader>
            <CardBody>
                <Dropzone
                    ref={node => { this.dropzoneRef = node }}
                    className={`soubory-dropzone${!hasSoubory || dragActive ? " empty" : ""}`}
                    getDataTransferItems={evt => getDroppedOrSelectedFiles(evt).then(list => {
                        this.setState({ droppedFolder: list });
                        return list.map(({ fileObject, ...rest }) => fileObject);
                    })}
                    onDrop={this.handleFilesDropped}
                    onDragEnter={this.handleDragEnter}
                    onDragLeave={this.handleDragLeave}
                    disableClick
                    multiple={true}
                >
                    {dropzoneContent}
                </Dropzone>
            </CardBody>
        </Card>
    }
}

function mapStateToProps(state) {
    return {
        authInfo: utils.storeFromArea(state, authActions.AUTH_INFO_AREA),
        souborAtributyList: utils.storeFromArea(state, ciselnikActions.SOUBOR_ATRIBUTY_LIST_AREA),
    }
}

export default connect(mapStateToProps)(withTranslation()(FileUploadPanel));
